
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Eye, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Use the database types directly from Supabase
type DatabaseErrorLog = {
  id: string;
  error_type: string;
  error_message: string;
  error_details: any;
  user_id?: string;
  booking_id?: string;
  session_id?: string;
  created_at: string;
  severity: string;
  status: string;
  user_email?: string;
  booking_number?: string;
  resolved_at?: string;
  resolved_by?: string;
  resolution_notes?: string;
  updated_at: string;
};

// Type-safe error types
const ERROR_TYPES = ['payment', 'booking', 'membership', 'system', 'authentication'] as const;
const SEVERITY_LEVELS = ['low', 'medium', 'high', 'critical'] as const;
const STATUS_TYPES = ['new', 'investigating', 'resolved', 'ignored'] as const;

type ErrorType = typeof ERROR_TYPES[number];
type SeverityLevel = typeof SEVERITY_LEVELS[number];
type StatusType = typeof STATUS_TYPES[number];

export const ErrorLogsPanel = () => {
  const [errorLogs, setErrorLogs] = useState<DatabaseErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState<DatabaseErrorLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { toast } = useToast();

  const fetchErrorLogs = async () => {
    try {
      setLoading(true);
      
      const { data: errorLogsData, error } = await supabase
        .from('error_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      console.log('Fetched error logs:', errorLogsData);
      setErrorLogs(errorLogsData || []);
    } catch (error) {
      console.error('Error fetching error logs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch error logs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchErrorLogs();
  }, []);

  const updateErrorStatus = async (errorId: string, newStatus: string, notes?: string) => {
    try {
      const { error } = await supabase
        .from('error_logs')
        .update({ 
          status: newStatus,
          resolution_notes: notes || null
        })
        .eq('id', errorId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Error status updated to ${newStatus}`,
      });

      // Refresh the data
      fetchErrorLogs();
      
      // Update selected error if it's the one being updated
      if (selectedError && selectedError.id === errorId) {
        setSelectedError({
          ...selectedError,
          status: newStatus,
          resolution_notes: notes || selectedError.resolution_notes
        });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update error status",
        variant: "destructive",
      });
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'payment': return 'bg-blue-100 text-blue-800';
      case 'booking': return 'bg-purple-100 text-purple-800';
      case 'membership': return 'bg-indigo-100 text-indigo-800';
      case 'system': return 'bg-gray-100 text-gray-800';
      case 'authentication': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'investigating': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'ignored': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredLogs = errorLogs.filter(log => {
    if (filterType !== 'all' && log.error_type !== filterType) return false;
    if (filterSeverity !== 'all' && log.severity !== filterSeverity) return false;
    if (filterStatus !== 'all' && log.status !== filterStatus) return false;
    return true;
  });

  const handleViewDetails = (error: DatabaseErrorLog) => {
    setSelectedError(error);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <CardTitle>Error Logs</CardTitle>
            </div>
            <div className="flex items-center space-x-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="booking">Booking</SelectItem>
                  <SelectItem value="membership">Membership</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="authentication">Auth</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="ignored">Ignored</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={fetchErrorLogs}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading error logs...</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Error Message</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No error logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Badge className={getTypeColor(log.error_type)}>
                          {log.error_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getSeverityColor(log.severity)}>
                          {log.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(log.status)}>
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {log.error_message}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {log.user_email || 'Unknown'}
                          {log.booking_number && (
                            <div className="text-xs text-gray-500">
                              Booking: {log.booking_number}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {new Date(log.created_at).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {log.status === 'new' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateErrorStatus(log.id, 'investigating')}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              Investigate
                            </Button>
                          )}
                          {log.status === 'investigating' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateErrorStatus(log.id, 'resolved')}
                              className="text-green-600 hover:text-green-700"
                            >
                              Resolve
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Error Details</DialogTitle>
          </DialogHeader>
          {selectedError && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold">Type:</label>
                  <Badge className={getTypeColor(selectedError.error_type)}>
                    {selectedError.error_type}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-semibold">Severity:</label>
                  <Badge className={getSeverityColor(selectedError.severity)}>
                    {selectedError.severity}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-semibold">Status:</label>
                  <div className="flex items-center space-x-2">
                    <Badge className={getStatusColor(selectedError.status)}>
                      {selectedError.status}
                    </Badge>
                    <Select 
                      value={selectedError.status} 
                      onValueChange={(newStatus) => updateErrorStatus(selectedError.id, newStatus)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="investigating">Investigating</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="ignored">Ignored</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold">Error Message:</label>
                <p className="text-sm mt-1 p-2 bg-gray-50 rounded">{selectedError.error_message}</p>
              </div>
              
              <div>
                <label className="text-sm font-semibold">User Email:</label>
                <p className="text-sm mt-1">{selectedError.user_email || 'N/A'}</p>
              </div>
              
              {selectedError.booking_number && (
                <div>
                  <label className="text-sm font-semibold">Booking Number:</label>
                  <p className="text-sm mt-1">{selectedError.booking_number}</p>
                </div>
              )}
              
              {selectedError.session_id && (
                <div>
                  <label className="text-sm font-semibold">Session ID:</label>
                  <p className="text-sm mt-1 font-mono">{selectedError.session_id}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-semibold">Date:</label>
                <p className="text-sm mt-1">{new Date(selectedError.created_at).toLocaleString()}</p>
              </div>

              {selectedError.resolved_at && (
                <div>
                  <label className="text-sm font-semibold">Resolved At:</label>
                  <p className="text-sm mt-1">{new Date(selectedError.resolved_at).toLocaleString()}</p>
                </div>
              )}

              {selectedError.resolution_notes && (
                <div>
                  <label className="text-sm font-semibold">Resolution Notes:</label>
                  <p className="text-sm mt-1 p-2 bg-gray-50 rounded">{selectedError.resolution_notes}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-semibold">Error Details:</label>
                <pre className="text-xs mt-1 bg-gray-100 p-3 rounded overflow-x-auto max-h-40">
                  {JSON.stringify(selectedError.error_details, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
